import React, { useState, useRef, useEffect } from 'react';
import AOS from 'aos';
import '../styles/pages/OurDate.css';
import Navbar from '../components/layout/Navbar';

// Import images
import Banner from '../components/assets/Ourdate/Ourdate_Banner.png';
import Concept from '../components/assets/Ourdate/Concepts.png';
import BusinessDashboard from '../components/assets/Ourdate/Business_dashboard.gif';

// Import flow GIFs
import CommunityBasedFlow from '../components/assets/Ourdate/Community_based_flow.gif';
import ActivityBasedFlow from '../components/assets/Ourdate/Activity_based_flow.gif';
import EventBasedFlow from '../components/assets/Ourdate/Event_based_flow.gif';

const OurDate = () => {
  const [selectedConcept, setSelectedConcept] = useState('Community based');
  const [activeTab, setActiveTab] = useState('flow');
  const [expandedConcept, setExpandedConcept] = useState('Community based');
  const [isMobile, setIsMobile] = useState(false);
  const [isFeaturesMobile, setIsFeaturesMobile] = useState(false);
  const progressRef = useRef(null);
  const progressMobileRef = useRef(null);

  const concepts = [
    {
      name: 'Community based',
      flow: CommunityBasedFlow,
      flowSteps: [
        'Join Communities to meet more people',
        'Propose a date activity and get responses',
        'Choose a person based on responses',
        'Get access to personal chat'
      ],
      story: {
        idea: 'This flow is proposed to meet people through communities based on their interests. They can interact and connect with like-minded people on this platform. They can propose dates through a date questionnaire and choose a date partner based on their responses. They can chat with their date partners without disclosing their personal details.',
        didntWork: 'Usability tests reflected that the users found the entire process complicated and counter-intuitive. It didn\'t align with their natural expectations from a dating app.'
      }
    },
    {
      name: 'Activity based',
      flow: ActivityBasedFlow,
      flowSteps: [
        'Join Activities to meet more people',
        'Get access to the participants\' profiles',
        'Choose a person based on interactions',
        'Get access to personal chat'
      ],
      story: {
        idea: 'This flow is proposed to overcome the problems found in the community-based dating user flow. The idea is to create an environment to facilitate organic relationships. Users can connect through well-planned activities. They are given only a few activity suggestions every week to enforce mindful scrolling. They can get calculated insights on who might be a good match for them with the details provided during onboarding. They can chat with people only after mutual consent.',
        didntWork: 'Most of them mentioned how this actually positively enforces them to consciously date and connect based on favorite events. However the regular swiping of the dating apps was extremely simple, we realised we needed something really different.'
      }
    },
    {
      name: 'Event based',
      flow: EventBasedFlow,
      flowSteps: [
        'Join Events to meet more people',
        'Get access to group chat and profile',
        'Attend events and check the vibe match',
        'Get access to personal chat'
      ],
      story: {
        idea: 'We wanted to create something that encourages people to step out of their homes and meet others in real life. While the previous flow worked technically, it didn\'t help us grow a strong user base. To build a more engaging MVP, we decided to center the experience around curated dating events—giving users a reason to connect beyond just profiles. The app now has two clear entry points: business owners can list their events, and daters can browse events and see who else is attending. This setup not only adds context to interactions but also creates a dynamic ecosystem where events drive social discovery.',
        worked: 'Users found the concept refreshing. The curated events created natural settings for conversation and reduced the pressure of typical dating apps. People were more inclined to interact when they shared a common activity, and businesses appreciated the added visibility and engagement.'
      }
    }
  ];

  const selectedConceptData = concepts.find(c => c.name === selectedConcept);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 700);
      setIsFeaturesMobile(window.innerWidth <= 900);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('.ourdate-content section, .ourdate-content > div[data-aos]');
      let viewed = 0;
      const buffer = window.innerHeight * 0.2; // Reduced buffer for more accurate detection
      
      sections.forEach(section => {
        const rect = section.getBoundingClientRect();
        if (rect.top < window.innerHeight - buffer) viewed++;
      });
      
      // Ensure we reach 100% when at the bottom
      const scrollPosition = window.scrollY + window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollPercent = scrollPosition / documentHeight;
      
      // Use the higher of the two calculations to ensure we reach 100%
      const sectionPercent = Math.max(0, Math.min(1, viewed / sections.length));
      const percent = Math.max(sectionPercent, scrollPercent);
      
      console.log('Progress Debug:', {
        sections: sections.length,
        viewed: viewed,
        sectionPercent: sectionPercent,
        scrollPercent: scrollPercent,
        finalPercent: percent,
        windowWidth: window.innerWidth,
        isMobile: window.innerWidth <= 700
      });
      
      if (progressRef.current) {
        progressRef.current.style.height = `${percent * 100}%`;
      }
      if (progressMobileRef.current) {
        if (window.innerWidth <= 700) {
          progressMobileRef.current.style.width = `${percent * 100}%`;
          console.log('Mobile progress bar updated to:', `${percent * 100}%`);
        }
      }
    };
    
    // Prevent automatic scrolling on page load
    window.scrollTo(0, 0);
    
    window.addEventListener('scroll', handleScroll);
    // Delay the initial handleScroll call to prevent immediate scroll behavior
    setTimeout(() => {
      handleScroll();
    }, 100);
    
    AOS.refresh();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    AOS.init({ once: false, duration: 700, easing: 'ease-out-cubic' });
  }, []);

  const handleConceptSelect = (conceptName) => {
    setSelectedConcept(conceptName);
    setExpandedConcept(conceptName);
    setActiveTab('flow');
  };

  return (
    <div className="ourdate-page">
      <Navbar />
      {/* Progress indicator */}
      <div className="ourdate-progress-indicator">
        <div ref={progressRef} style={{ width: '100%', height: 0 }}></div>
      </div>
      <div className="ourdate-progress-indicator-mobile">
        <div ref={progressMobileRef} style={{ width: 0 }}></div>
      </div>

      {/* Banner */}
      <img src={Banner} alt="OurDate Banner" className="ourdate-header" data-aos="fade-up" />

      <div className="ourdate-content">
        {/* About Section */}
        <section data-aos="fade-up" className="ourdate-about-section">
          <h2>ABOUT OURDATE</h2>
          <p>A Dating Application that brings people closer while doing what they love. You don't meet a profile, but you meet a person. You do the activity first, and then you decide if you strike a vibe with them. In this 3.5-month-long internship, the design team mainly worked on creating a safe, simple, and conscious dating experience. We had to design something that breaks the old swiping dating pattern and introduces sensible dating.</p>
        </section>

        {/* Onboarding Section */}
        <section data-aos="fade-up" className="ourdate-onboarding-section">
          <h2>SECTIONS</h2>
          <div className="ourdate-onboarding-grid">
            <div className="ourdate-onboarding-item">
              <h3>Onboarding</h3>
              <div className="ourdate-goals-needs">
                <div className="ourdate-goals">
                  <h4>Goals</h4>
                  <p>Obtain Personal Details, and Date requirements</p>
                </div>
                <div className="ourdate-needs">
                  <h4>Needs</h4>
                  <p>Simple and quick</p>
                </div>
              </div>
            </div>
            
            <div className="ourdate-onboarding-item ourdate-date-proposal">
              <div className="ourdate-worked-indicator">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 0L12.245 7.755L20 10L12.245 12.245L10 20L7.755 12.245L0 10L7.755 7.755L10 0Z" fill="#50ABBC"/>
                </svg>
                <span>I worked on this section</span>
              </div>
              <h3>Date proposal</h3>
              <div className="ourdate-goals-needs">
                <div className="ourdate-goals">
                  <h4>Goals</h4>
                  <p>Connect with people and plan dates</p>
                </div>
                <div className="ourdate-needs">
                  <h4>Needs</h4>
                  <p>Intuitive and clear</p>
                </div>
              </div>
            </div>
            
            <div className="ourdate-onboarding-item">
              <h3>Feedback</h3>
              <div className="ourdate-goals-needs">
                <div className="ourdate-goals">
                  <h4>Goals</h4>
                  <p>Understand users app experience</p>
                </div>
                <div className="ourdate-needs">
                  <h4>Needs</h4>
                  <p>Short and prompt</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Concepts Section */}
        <section data-aos="fade-up" className="ourdate-concepts-section">
          <h2>CONCEPTS</h2>
          <p>We were still exploring a few concepts to find the true purpose of OurDate. We designed different screens, user flows for each phase, and conducted regular usability tests to validate a few research-derived hypotheses.</p>
          <img src={Concept} alt="Concept Exploration" className="ourdate-concept-img" />
        </section>

        {/* Features Section */}
        <section data-aos="fade-up" className="ourdate-features-section">
          <h2>CONCEPT EXPLORATION</h2>
          <p>We explored three different approaches to create a meaningful dating experience that goes beyond traditional swiping.</p>
          
          <div className="ourdate-features-container">
            {/* Left Column - Concept List */}
            <div className="ourdate-features-list">
              {concepts.map((concept) => (
                <div key={concept.name} className="ourdate-feature-accordion-item">
                  <button
                    className={`ourdate-feature-item ${selectedConcept === concept.name ? 'active' : ''}`}
                    onClick={() => handleConceptSelect(concept.name)}
                  >
                    {concept.name}
                  </button>
                  
                  {/* Mobile Accordion Content */}
                  {isFeaturesMobile && (
                    <div className={`ourdate-feature-mobile-content ${expandedConcept === concept.name ? 'expanded' : ''}`}>
                      <div className="ourdate-feature-tabs">
                        <button
                          className={`ourdate-tab ${activeTab === 'flow' ? 'active' : ''}`}
                          onClick={() => setActiveTab('flow')}
                        >
                          Flow
                        </button>
                        <button
                          className={`ourdate-tab ${activeTab === 'story' ? 'active' : ''}`}
                          onClick={() => setActiveTab('story')}
                        >
                          Story
                        </button>
                      </div>

                      <div className="ourdate-feature-content">
                        {activeTab === 'flow' && (
                          <div className="ourdate-flow-container">
                            <img src={concept.flow} alt={`${concept.name} Flow`} />
                            <div className="ourdate-flow-steps">
                              <h3>PROPOSED FLOW</h3>
                              {concept.flowSteps.map((step, index) => (
                                <div key={index} className="ourdate-flow-step">
                                  <span>{step}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {activeTab === 'story' && (
                          <div className="ourdate-story-container">
                            <h3>IDEA BEHIND THE FLOW</h3>
                            <p>{concept.story.idea}</p>
                            {concept.story.didntWork && (
                              <>
                                <h4>WHAT DIDN'T WORK?</h4>
                                <p>{concept.story.didntWork}</p>
                              </>
                            )}
                            {concept.story.worked && (
                              <>
                                <h4>WHAT WORKED?</h4>
                                <p>{concept.story.worked}</p>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Desktop Feature Details */}
            {!isFeaturesMobile && (
              <div className="ourdate-feature-details">
                <div className="ourdate-feature-tabs">
                  <button
                    className={`ourdate-tab ${activeTab === 'flow' ? 'active' : ''}`}
                    onClick={() => setActiveTab('flow')}
                  >
                    Flow
                  </button>
                  <button
                    className={`ourdate-tab ${activeTab === 'story' ? 'active' : ''}`}
                    onClick={() => setActiveTab('story')}
                  >
                    Story
                  </button>
                </div>

                <div className="ourdate-feature-content">
                  {activeTab === 'flow' && selectedConceptData && (
                    <div className="ourdate-flow-container">
                      <img src={selectedConceptData.flow} alt={`${selectedConcept} Flow`} />
                      <div className="ourdate-flow-steps">
                        <h3>PROPOSED FLOW</h3>
                        {selectedConceptData.flowSteps.map((step, index) => (
                          <div key={index} className="ourdate-flow-step">
                            <span>{step}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {activeTab === 'story' && selectedConceptData && (
                    <div className="ourdate-story-container">
                      <h3>IDEA BEHIND THE FLOW</h3>
                      <p>{selectedConceptData.story.idea}</p>
                      {selectedConceptData.story.didntWork && (
                        <>
                          <h4>WHAT DIDN'T WORK?</h4>
                          <p>{selectedConceptData.story.didntWork}</p>
                        </>
                      )}
                      {selectedConceptData.story.worked && (
                        <>
                          <h4>WHAT WORKED?</h4>
                          <p>{selectedConceptData.story.worked}</p>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Business Dashboard Section */}
        <section data-aos="fade-up" className="ourdate-dashboard-section">
          <h2>BUSINESS OWNERS DASHBOARD</h2>
          <p>I gradually shifted to the business owner dashboard design. This is the revenue-generating aspect of OurDate where we integrate with different business owners who can promote their activities on the OurDate app. We wanted to create a dashboard where the owners can view analytics of each event as well as the business.</p>
          
          <div className="ourdate-dashboard-requirements">
            <div className="ourdate-requirement-item">
              <h3>Business analytics</h3>
              <p>The business analytics dashboard has information regarding cumulative ticket sales, demographics, event metrics, revenue generated, feedback analysis, etc. I also designed different filters that can be useful for the business owners to make well-informed marketing decisions.</p>
            </div>
            <div className="ourdate-requirement-item">
              <h3>Event analytics</h3>
              <p>The event analytics dashboard has information regarding ticket sales, customer demographics, RSVP click ratio, etc. The event participants list can also be viewed on this page. They can also view event-related queries on this dashboard. This will be the one-stop information intersection between business owners and OurDate users.</p>
            </div>
          </div>
          
          <img src={BusinessDashboard} alt="Business Dashboard" className="ourdate-dashboard-img" />
        </section>

        {/* Reflections Section */}
        <section data-aos="fade-up" className="ourdate-reflections-section">
          <h2>REFLECTIONS</h2>
          <ul>
            <li>Working in a collaborative, cross-functional environment with teams from business, marketing, development, finance, and design fostered a sense of harmony. Within the design team, comprising UX designers, UI designers, graphic designers, and content strategists, we had the opportunity to explore various dimensions of user experience.</li>
            <li>Engaging in time-bound design iterations not only kept us on our toes but also enhanced our design speed. It challenged us to think creatively while adhering to strict timelines.</li>
            <li>The accelerated concept exploration process enabled us to gather diverse perspectives and ultimately deliver the best possible outcomes. By considering multiple viewpoints, we ensured that our designs were comprehensive and well-rounded.</li>
            <li>To stay ahead of market trends and meet user requirements, we conducted continuous user and market research. This ongoing effort allowed us to remain informed about evolving market dynamics and incorporate user insights into our design decisions.</li>
            <li>Overall, this dynamic and collaborative work environment enabled us to thrive as a design team, constantly improving our skills and delivering impactful solutions.</li>
          </ul>
        </section>
      </div>
    </div>
  );
};

export default OurDate; 